import type { PublicRateCard, PublicRateLine, UsageChargeType } from "./rateCardTypes";

export interface EstimateInput {
    aiMinutes: number;
    transfers: number;
    phoneNumbers: number;
    agentMinutes: number;
    callCentreNumbers: number;
    smsSegments: number;
    /** Which carrier's AI rate to use; ignored where one rate covers every carrier. */
    carrier?: string | null;
}

export interface EstimateLine {
    chargeType: UsageChargeType;
    quantity: number;
    unitMinor: number;
    totalMinor: number;
    currency: string;
}

export interface UsageEstimate {
    lines: EstimateLine[];
    /** Per currency, because SMS can be priced in a different one from voice. */
    totals: Record<string, number>;
    /** Monthly saving a committed-volume rate would give at this volume, or null when none applies. */
    committedSavingsMinor: number | null;
}

const whole = (n: number) => (Number.isFinite(n) && n > 0 ? Math.floor(n) : 0);

function pick(lines: PublicRateLine[], chargeType: UsageChargeType, carrier?: string | null): PublicRateLine | null {
    const matching = lines.filter((l) => l.chargeType === chargeType);
    return matching.find((l) => l.carrier === carrier) ?? matching.find((l) => l.carrier === null) ?? matching[0] ?? null;
}

/**
 * A month's usage priced at list rates. Credits and negotiated rates are not
 * applied — this is what the usage costs before either.
 */
export function estimateMonthlyUsage(card: PublicRateCard, input: EstimateInput): UsageEstimate {
    const carrier = input.carrier ?? card.voice.find((l) => l.chargeType === "VOICE_PER_MINUTE")?.carrier ?? null;
    const wanted: Array<[PublicRateLine | null, number]> = [
        [pick(card.voice, "VOICE_PER_MINUTE", carrier), whole(input.aiMinutes)],
        [pick(card.voice, "CALL_TRANSFER", carrier), whole(input.transfers)],
        [pick(card.voice, "NUMBER_RENTAL", carrier), whole(input.phoneNumbers)],
        [pick(card.callCentre, "CALL_CENTRE_MINUTE"), whole(input.agentMinutes)],
        [pick(card.callCentre, "CALL_CENTRE_RENTAL"), whole(input.callCentreNumbers)],
        [pick(card.sms.filter((l) => l.account !== "own"), "SMS"), whole(input.smsSegments)],
    ];

    const lines: EstimateLine[] = [];
    const totals: Record<string, number> = {};
    for (const [rate, quantity] of wanted) {
        if (!rate || quantity <= 0) continue;
        const totalMinor = rate.amountMinor * quantity;
        lines.push({ chargeType: rate.chargeType, quantity, unitMinor: rate.amountMinor, totalMinor, currency: rate.currency });
        totals[rate.currency] = (totals[rate.currency] ?? 0) + totalMinor;
    }

    const voiceRate = pick(card.voice, "VOICE_PER_MINUTE", carrier);
    const committed = card.committedVoice;
    const aiMinutes = whole(input.aiMinutes);
    const committedSavingsMinor =
        committed && voiceRate && aiMinutes >= committed.minMonthlyMinutes && committed.perMinuteMinor < voiceRate.amountMinor
            ? (voiceRate.amountMinor - committed.perMinuteMinor) * aiMinutes
            : null;

    return { lines, totals, committedSavingsMinor };
}
